import React, { useState } from "react";
import {
    DialogContent,
    DialogActions,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    Grid,
    FormControlLabel,
    Button,
    Switch,
    Typography,
    useMediaQuery,
    Stack,
    useTheme,
    ThemeProvider,
    Fab,
    Tooltip,
    CircularProgress
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
    listProviderSchedules,
    locationByProvider
} from "../../graphql/queries";
import AvailabilityModal from "./AvailabilityModal";
import { useAtom } from "jotai";
import {
    selectedUserAtom,
    selectedUserSchedulesAtom,
    selectedAvailabilityScIdAtom,
    openAvailabilityDialogAtom,
    selectedProviderSchedulesAtom,
    selectedUserProviderLocationsAtom
} from "../../atoms/usersTable";
import { recordIdAtom } from "../../atoms/blockedTime";
import { daysArrayToString } from "../../modules/RecurringBookingsUtilityFunctions";
import AddIcon from "@mui/icons-material/Add";
import moment from "moment";
import MobileScheduleCard from "../../components/mobile/MobileScheduleCard";
import { MobileBookingsTheme } from "../../styles/MobileBookingsTheme";
import { execReadBySortkey } from "../../modules/DBService";
import {
    allActiveLocationsAtom,
    isMbxUserManager,
    mbxUserAtom
} from "../../atoms/atoms";
import _ from "lodash";

const AvailabilityTab = ({ onClose }) => {
    const theme = useTheme();
    const [mbxUser] = useAtom(mbxUserAtom);
    const [isLoggedInUserManager] = useAtom(isMbxUserManager);
    const [openAvailabilityDialog, setOpenAvailabilityDialog] = useAtom(
        openAvailabilityDialogAtom
    );
    const [selectedUser] = useAtom(selectedUserAtom);
    const [selectedUserProviderLocations, setSelectedUserProviderLocations] =
        useAtom(selectedUserProviderLocationsAtom);
    const [recordId] = useAtom(recordIdAtom);
    const [selectedUserSchedules] = useAtom(selectedUserSchedulesAtom);
    const [allActiveLocations] = useAtom(allActiveLocationsAtom);
    const [, setSelectedAvailabilityScId] = useAtom(
        selectedAvailabilityScIdAtom
    );
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });
    const [, setSelectedProviderSchedules] = useAtom(
        selectedProviderSchedulesAtom
    );
    const [showExpired, setShowExpired] = useState(false);
    const [openTooltipIndex, setOpenTooltipIndex] = useState(false); // State to track which tooltip is open
    const handleOpenAvailabilityDialog = () => {
        setOpenAvailabilityDialog(true);
    };
    const isManagerView =
        isLoggedInUserManager &&
        mbxUser.id !== selectedUser.id &&
        selectedUser.role !== "PROVIDER";

    const disableAllFields = isManagerView;
    const { refetch, isFetching } = useQuery({
        queryKey: ["get-provider-schedules"],
        queryFn: async () => {
            try {
                let { items, error } = await execReadBySortkey({
                    opname: "listProviderSchedules",
                    op: listProviderSchedules,
                    id: {
                        id: recordId
                    },
                    skey: {
                        scheduleinfo: { beginsWith: "S" }
                    },
                    filter: {
                        deleted: { ne: true },
                        active: { ne: false }
                    },
                    limit: 500
                });

                if (error) throw error;
                return items;
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (providerSchedules) => {
            setSelectedProviderSchedules(providerSchedules);
        }
    });

    const { isFetching: isFetchingProviderLocations } = useQuery({
        queryKey: [`get-provider-location-${selectedUser?.provider?.id}`],
        queryFn: async () => {
            try {
                let { items, error } = await execReadBySortkey({
                    opname: "locationByProvider",
                    op: locationByProvider,
                    id: {
                        providerId: selectedUser?.provider?.id
                    },
                    filter: {
                        deleted: { ne: true },
                        active: { ne: false }
                    },
                    limit: 500
                });
                if (error) throw error;
                return items;
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (providerLocations) => {
            setSelectedUserProviderLocations(providerLocations);
        }
    });

    const handleCloseAvailabilityDialog = () => {
        setOpenAvailabilityDialog(false);
    };

    const ServicesTableCell = ({ row, index, providerServices, variant }) => {
        //Parse, extract and validate schedule services with provider services
        const rowServices = JSON.parse(row.services);
        const validServices = rowServices
            .filter((serviceId) =>
                providerServices.some(
                    (service) => service.servicetype.id === serviceId
                )
            )
            .map((serviceId) => {
                const serviceName = providerServices.find(
                    (service) => service.servicetype.id === serviceId
                ).servicetype.name;

                return {
                    id: serviceId,
                    name: serviceName
                };
            });

        const allServicesMatch =
            validServices.length === providerServices.length;
        const validSortedServices = _.sortBy(
            validServices,
            (item) => item?.name?.toLowerCase() || ""
        );
        return (
            <TableCell
                variant={variant || ""}
                sx={
                    index === lastRowIndex
                        ? {
                              borderBottom: "none"
                          }
                        : {}
                }
            >
                <Tooltip
                    title={
                        validServices.length > 0 && !allServicesMatch ? (
                            <Stack>
                                {validSortedServices.map((service) => (
                                    <Typography key={service.id}>
                                        {service.name}
                                    </Typography>
                                ))}
                            </Stack>
                        ) : (
                            ""
                        )
                    }
                    open={openTooltipIndex === index} // Check if this index is the one open
                    onOpen={() => setOpenTooltipIndex(index)}
                    onClose={() => setOpenTooltipIndex(false)}
                    placement="top"
                >
                    <Typography
                        onMouseEnter={() => setOpenTooltipIndex(index)}
                        onMouseLeave={() => setOpenTooltipIndex(false)}
                    >
                        {allServicesMatch
                            ? "All"
                            : `${validServices.length} / ${providerServices.length}`}
                    </Typography>
                </Tooltip>
            </TableCell>
        );
    };

    const CoverageAreaCell = ({ locations, cellIndex }) => {
        let travelTypeText;
        let companyLocations = [];

        const plLocation = locations.find((location) =>
            location.startsWith("PL-")
        );
        if (plLocation) {
            const id = plLocation.split("PL-")[1];
            const providerLocation = selectedUserProviderLocations?.find(
                (loc) => loc.id === id
            );

            if (
                providerLocation &&
                providerLocation.maxtraveltype === "DRAW_TRAVEL_REGION"
            ) {
                travelTypeText = "Drawn zone";
            } else {
                travelTypeText = "Circular zone";
            }
        } else {
            companyLocations = _(locations)
                .map((locationStr) => locationStr.slice(3)) //extract locationId from 'CL-{locationId}'
                .map(
                    (locationId) =>
                        _.find(allActiveLocations, { id: locationId })
                            ?.locationname
                )
                .compact()
                .value();

            travelTypeText =
                companyLocations.length === 1
                    ? companyLocations[0]
                    : `${companyLocations.length} locations`;
        }
        return (
            <Tooltip
                title={
                    companyLocations.length > 1 ? (
                        <Stack>
                            {companyLocations.map((name) => (
                                <Typography key={name}>{name}</Typography>
                            ))}
                        </Stack>
                    ) : (
                        ""
                    )
                }
                disableInteractive
            >
                <TableCell
                    sx={
                        cellIndex === lastRowIndex
                            ? {
                                  borderBottom: "none"
                              }
                            : {}
                    }
                >
                    {travelTypeText}
                </TableCell>
            </Tooltip>
        );
    };

    const handleRowClick = (id) => {
        if (!id) return;
        setSelectedAvailabilityScId(id);
        setOpenAvailabilityDialog(true);
        setOpenTooltipIndex(false);
    };

    function sortSchedules(arr) {
        // Sort each SB array by startDate and startTime
        arr.forEach((schedule) => {
            schedule.SB.sort((a, b) => {
                const startDateTimeA = new Date(
                    `${a.startDate}T${a.startTime}`
                ).getTime();
                const startDateTimeB = new Date(
                    `${b.startDate}T${b.startTime}`
                ).getTime();
                return startDateTimeA - startDateTimeB;
            });
        });

        // Sort the main array of schedules based on the earliest start and end times
        return arr.sort((a, b) => {
            const getEarliestStartDateTime = (schedule) => {
                return Math.min(
                    ...schedule.SB.map((item) =>
                        new Date(
                            `${item.startDate}T${item.startTime}`
                        ).getTime()
                    )
                );
            };

            const getEarliestEndDateTime = (schedule) => {
                return Math.min(
                    ...schedule.SB.map((item) =>
                        new Date(`${item.endDate}T${item.endTime}`).getTime()
                    )
                );
            };

            const startDateTimeA = getEarliestStartDateTime(a);
            const startDateTimeB = getEarliestStartDateTime(b);

            // Compare startDateTime ASC
            if (startDateTimeA !== startDateTimeB) {
                return startDateTimeA - startDateTimeB;
            }

            const endDateTimeA = getEarliestEndDateTime(a);
            const endDateTimeB = getEarliestEndDateTime(b);

            // Compare endDateTime ASC
            if (endDateTimeA !== endDateTimeB) {
                return endDateTimeA - endDateTimeB;
            }

            // Compare createdAt DESC
            return (
                new Date(b.SB[0].createdAt).getTime() -
                new Date(a.SB[0].createdAt).getTime()
            );
        });
    }

    const getVisibleSchedules = () => {
        let visibleSchedules = [];
        if (showExpired) {
            visibleSchedules = selectedUserSchedules || [];
        } else {
            visibleSchedules =
                selectedUserSchedules?.filter((schedule) =>
                    schedule?.SB.some(
                        (item) => item.endDate >= moment().format("YYYY-MM-DD")
                    )
                ) || [];
        }

        if (visibleSchedules.length) {
            visibleSchedules = sortSchedules(visibleSchedules);
        }
        return visibleSchedules;
    };

    const lastRowIndex = getVisibleSchedules().length - 1; // Index of the last row

    function isLoading() {
        //true if query is actively fetching && the user has atleast 1 schedule && we havent already loaded the selected users schedules into memory
        return (
            isFetching &&
            isFetchingProviderLocations &&
            selectedUser?.availability?.length > 0 &&
            selectedUserSchedules?.length === 0
        );
    }

    return (
        <>
            <AvailabilityModal
                open={openAvailabilityDialog}
                onClose={() => {
                    handleCloseAvailabilityDialog();
                    refetch();
                }}
            />

            <DialogContent sx={{ minHeight: "200px" }}>
                {isDesktop ? (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date range</TableCell>
                                    <TableCell sx={{ width: "25%" }}>
                                        Time range
                                    </TableCell>
                                    <TableCell sx={{ width: "15%" }}>
                                        Days
                                    </TableCell>
                                    <TableCell sx={{ width: "15%" }}>
                                        Coverage area
                                    </TableCell>
                                    <TableCell>Services</TableCell>
                                </TableRow>
                            </TableHead>
                            {isLoading() ? (
                                <TableBody>
                                    <TableRow>
                                        <TableCell
                                            colSpan={5}
                                            sx={{
                                                textAlign: "center",
                                                padding: "32px !important",
                                                borderBottom: "none"
                                            }}
                                        >
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            ) : (
                                <TableBody>
                                    {getVisibleSchedules().map((row, index) => (
                                        <TableRow
                                            key={row.scheduleinfo}
                                            hover={!disableAllFields}
                                            onClick={
                                                !disableAllFields
                                                    ? () =>
                                                          handleRowClick(
                                                              row.scheduleinfo
                                                          )
                                                    : undefined
                                            }
                                            sx={{
                                                cursor: !disableAllFields
                                                    ? "pointer"
                                                    : "default",
                                                opacity: !disableAllFields
                                                    ? 1
                                                    : 0.5,
                                                pointerEvents: !disableAllFields
                                                    ? "auto"
                                                    : "none"
                                            }}
                                        >
                                            <TableCell
                                                sx={
                                                    index === lastRowIndex
                                                        ? {
                                                              borderBottom:
                                                                  "none"
                                                          }
                                                        : {}
                                                }
                                            >
                                                <Grid
                                                    container
                                                    justifyContent="space-between"
                                                >
                                                    {row?.SB[0]?.startDate ===
                                                    row?.SB[0]?.endDate
                                                        ? `${moment(
                                                              row?.SB[0]
                                                                  ?.startDate
                                                          ).format(
                                                              "MMM D, YYYY"
                                                          )}`
                                                        : `${moment(
                                                              row?.SB[0]
                                                                  ?.startDate
                                                          ).format(
                                                              "MMM D, YYYY"
                                                          )} \u2014 ${moment(
                                                              row?.SB[0]
                                                                  ?.endDate
                                                          ).format(
                                                              "MMM D, YYYY"
                                                          )}`}
                                                </Grid>
                                            </TableCell>
                                            <TableCell
                                                sx={
                                                    index === lastRowIndex
                                                        ? {
                                                              borderBottom:
                                                                  "none"
                                                          }
                                                        : {}
                                                }
                                            >
                                                {row?.SB?.map((sb) => {
                                                    const startTime = moment(
                                                        sb?.startTime,
                                                        "HH:mm"
                                                    ).format("h:mm A");
                                                    const endTime = moment(
                                                        sb?.endTime,
                                                        "HH:mm"
                                                    ).format("h:mm A");

                                                    // Check if the time range is "12:00 AM" to "11:59 AM"
                                                    if (
                                                        startTime ===
                                                            "12:00 AM" &&
                                                        endTime === "11:59 PM"
                                                    ) {
                                                        return "All day\n";
                                                    }
                                                    if (
                                                        endTime === "11:59 PM"
                                                    ) {
                                                        return `${startTime} \u2014 Midnight \n`;
                                                    }

                                                    return `${startTime} \u2014 ${endTime} \n`;
                                                })}
                                            </TableCell>
                                            <TableCell
                                                sx={
                                                    index === lastRowIndex
                                                        ? {
                                                              borderBottom:
                                                                  "none"
                                                          }
                                                        : {}
                                                }
                                            >
                                                {row?.SB?.length &&
                                                    daysArrayToString(
                                                        row?.SB[0]?.weekDays
                                                            .split("")
                                                            .map(Number)
                                                    )}
                                            </TableCell>
                                            <CoverageAreaCell
                                                locations={JSON.parse(
                                                    row.locations
                                                )}
                                                cellIndex={index}
                                            />

                                            <ServicesTableCell
                                                row={row}
                                                index={index}
                                                providerServices={
                                                    selectedUser?.provider
                                                        ?.servicetypes?.items ||
                                                    []
                                                }
                                            />
                                        </TableRow>
                                    ))}
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>
                ) : (
                    <ThemeProvider theme={MobileBookingsTheme(theme)}>
                        {isLoading() ? (
                            <Grid container justifyContent={"center"} p={4}>
                                <CircularProgress />
                            </Grid>
                        ) : (
                            <Stack>
                                {getVisibleSchedules().map((row, index) => (
                                    <MobileScheduleCard
                                        schedule={row}
                                        key={row.scheduleinfo}
                                    />
                                ))}
                            </Stack>
                        )}
                        <Fab onClick={handleOpenAvailabilityDialog}>
                            <AddIcon
                                sx={{
                                    color: "white",
                                    fontSize: "2rem !important"
                                }}
                            />
                        </Fab>
                    </ThemeProvider>
                )}
            </DialogContent>
            {isDesktop && (
                <DialogActions
                    sx={{ display: "flex", justifyContent: "space-between" }}
                >
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showExpired}
                                onChange={(e) =>
                                    setShowExpired(e.target.checked)
                                }
                            />
                        }
                        label="Show expired schedules"
                    />

                    <Grid display={"flex"} gap={"18px"}>
                        <Button onClick={onClose} variant="cancelButton">
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon sx={{ color: "white" }} />}
                            onClick={handleOpenAvailabilityDialog}
                            disabled={disableAllFields}
                        >
                            <Typography
                                variant="button"
                                sx={{ fontWeight: "600", color: "white" }}
                            >
                                {" "}
                                Add Availability
                            </Typography>
                        </Button>
                    </Grid>
                </DialogActions>
            )}
        </>
    );
};

export default AvailabilityTab;
